// Reviewer blur-region editor (F5 privacy blurring, 2026-10-02).
// Opens over the review list on the UNBLURRED processed copy (reviewers
// only - the same audience as the original) and shows:
//   - automatic regions (faces, number plates) found on the server: click one
//     to stop blurring it (a false positive) or to blur it again;
//   - manual regions: drag on the photo to add one (whatever the detectors
//     missed), click one to remove it.
// Save re-renders the shared copy + thumbnail on the server (PUT .../regions).
// Boxes are normalised to the image (0..1), so they are resolution-free.
import { showToast } from "../toast";
import { h } from "./dom";
import type { BlurRegion, BlurRegions, Capture, CapturePhoto } from "./fieldApi";
import { fieldApi, photoUrl } from "./fieldApi";

type Box = [number, number, number, number];

export async function openBlurEditor(c: Capture, p: CapturePhoto, onSaved: () => void): Promise<void> {
  let state: BlurRegions;
  try {
    state = await fieldApi.blurRegions(c.id, p.id);
  } catch (e) {
    showToast(e instanceof Error ? e.message : "Could not load the blur regions");
    return;
  }
  const manual: BlurRegion[] = state.manual.map((m) => ({ kind: m.kind, box: m.box }));
  const suppressed = new Set(state.suppressed);

  const img = h("img", { src: photoUrl(c, p, "unblurred"), alt: "Photo (unblurred - reviewers only)", draggable: "false" });
  const layer = h("div", { class: "fb-layer" });
  const stage = h("div", { class: "fb-stage" }, img, layer);
  const summary = h("small", { class: "fb-summary" });
  const close = () => overlay.remove();

  const boxStyle = (b: Box) => `left:${b[0] * 100}%;top:${b[1] * 100}%;width:${b[2] * 100}%;height:${b[3] * 100}%`;

  function draw(): void {
    layer.replaceChildren();
    for (const a of state.auto) {
      const off = a.id !== undefined && suppressed.has(a.id);
      const el = h("div", {
        class: `fb-box fb-${a.kind}${off ? " fb-off" : ""}`,
        style: boxStyle(a.box),
        title: `${a.kind} (${a.source ?? "auto"}${a.score !== undefined ? `, ${a.score}` : ""}) - click to ${off ? "blur it again" : "NOT blur it"}`,
      });
      el.addEventListener("pointerdown", (e) => e.stopPropagation());
      el.addEventListener("click", () => {
        if (a.id === undefined) return;
        if (suppressed.has(a.id)) suppressed.delete(a.id);
        else suppressed.add(a.id);
        draw();
      });
      layer.append(el);
    }
    manual.forEach((m, i) => {
      const el = h("div", { class: "fb-box fb-manual", style: boxStyle(m.box), title: "Added by hand - click to remove" });
      el.addEventListener("pointerdown", (e) => e.stopPropagation());
      el.addEventListener("click", () => {
        manual.splice(i, 1);
        draw();
      });
      layer.append(el);
    });
    const nAuto = state.auto.length - [...suppressed].filter((id) => state.auto.some((a) => a.id === id)).length;
    summary.textContent = `${nAuto} automatic region(s) blurred, ${suppressed.size} switched off, ${manual.length} added by hand. Drag on the photo to add a region.`;
  }

  // drag to add a manual region
  let start: [number, number] | null = null;
  let ghost: HTMLElement | null = null;
  const rel = (e: PointerEvent): [number, number] => {
    const r = layer.getBoundingClientRect();
    return [Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))];
  };
  const norm = (a: [number, number], b: [number, number]): Box => [
    Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1]),
  ];
  layer.addEventListener("pointerdown", (e) => {
    start = rel(e);
    ghost = h("div", { class: "fb-box fb-manual fb-ghost" });
    layer.append(ghost);
    layer.setPointerCapture(e.pointerId);
    e.preventDefault();
  });
  layer.addEventListener("pointermove", (e) => {
    if (!start || !ghost) return;
    ghost.setAttribute("style", boxStyle(norm(start, rel(e))));
  });
  layer.addEventListener("pointerup", (e) => {
    if (!start) return;
    const b = norm(start, rel(e));
    start = null;
    ghost?.remove();
    ghost = null;
    if (b[2] > 0.01 && b[3] > 0.01) manual.push({ kind: "manual", box: b });
    draw();
  });

  const save = h("button", { class: "fa-btn fa-primary" }, "Save blur");
  save.addEventListener("click", async () => {
    save.setAttribute("disabled", "");
    try {
      await fieldApi.saveBlurRegions(c.id, p.id, manual, [...suppressed]);
      showToast("Blur saved - the shared copy was re-rendered");
      close();
      onSaved();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Save failed");
      save.removeAttribute("disabled");
    }
  });

  const overlay = h("div", { class: "fb-overlay", role: "dialog", "aria-label": "Edit photo blur" },
    h("div", { class: "fb-panel" },
      h("div", { class: "fb-head" },
        h("strong", {}, `Edit blur - capture #${c.id}, photo ${p.seq + 1}`),
        h("small", { class: "fa-muted" }, "Unblurred copy, reviewers only. Faces: red, plates: orange, your regions: blue, switched off: grey."),
      ),
      stage,
      summary,
      h("div", { class: "fr-actions" },
        save,
        h("button", { class: "fa-btn", onclick: () => { manual.length = 0; draw(); } }, "Clear my regions"),
        h("button", { class: "fa-btn", onclick: close }, "Cancel"),
      ),
    ));
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  document.body.append(overlay);
  draw();
}
